import {
  pagination_queries_interface,
  pagination_response_interface,
} from "./Core/Pagination";
import { productsInterface } from "./products";
import { getTranslatedSaleStatus } from "../translates/sales/sales";
import { TokenRedemption } from "./tokens";

export interface salesQueries extends pagination_queries_interface {
  seller?: string;
  concession?: string;
  event?: string;
  session?: string;
  sale_point?: string;
  status?: string;
  search_by?: string;
  search_value?: string;
  venue?: string;
  tags?: string;
  payment_method?: string;
}

export interface detailedSalesQueries {
  concession: string;
  event: string;
  session: string;
  venue: string;
  limit: number;
}

export interface salesListPagination extends pagination_response_interface {
  data: Sale[];
}

export interface Sale {
  products: productsInterface[];
  concession: Concession;
  created_at: string;
  event: Event;
  observations: string;
  seller: Seller;
  status: number;
  subtotal: number;
  tip: number;
  total: number;
  uuid: string;
  uuid_concession: string;
  uuid_event: string;
  uuid_seller: string;
  uuid_venue: string;
  total_products: number;
  fee: number;
  venue: Venue;
  seller_name: string;
  event_name: string;
  payment_method?: string;
}

interface Venue {
  uuid: string;
  short_name: string;
}

interface Seller {
  uuid: string;
  username: string;
  name: string;
}

interface Event {
  uuid: string;
  starts_at: string;
  ends_at: string;
  name: string;
}

interface Concession {
  uuid: string;
  image: string;
  name: string;
}

/////////

export interface Card {
  uuid: string;
  bin: string;
  last_four: string;
  brand: string;
  bank: string;
  type: string;
}

export interface NfcTransaction {
  transaction_id: string;
  uuid: string;
  uuid_sale: string;
  id_sale: number;
  previous_balance: number;
  post_balance: number;
  nfc_card: NfcCard;
}

interface NfcCard {
  uuid: string;
  serial_number: string;
  bin: string;
}

export interface Product {
  fee: number;
  product: string;
  price: number;
  base_price: number;
  quantity: number;
  subtotal: number;
  name_concession: string;
  uuid: string;
  uuid_concession: string;
  uuid_parent: string | null;
  uuid_product: string;
  uuid_sale: string;
  created_at: string;
  discount?: Discount;
  discountName: string;
  promotion?: Promotion;
  token_redemption?: TokenRedemption;
}

interface Discount {
  uuid: string;
  name: string;
  value: number;
  is_percent: boolean;
}

interface Promotion {
  uuid: string;
  name: string;
}

export enum SaleStatus {
  Cancelada = 0,
  Preautorizada = 1,
  Pagada = 2,
  "Reembolso pendiente" = 3,
  Reembolsada = 4,
}

export const saleStatus: { [key: string]: string } = {
  0: "Cancelada",
  1: "Preautorizada",
  2: "Pagada",
  3: "Reembolso pendiente",
  4: "Reembolsada",
  5: "Parcialmente pagada",
  6: "Cuenta Abierta",
  7: "Expirada",
};

// Helper function to get translated sale status
export const getSaleStatus = (status: number): string => {
  return getTranslatedSaleStatus(status);
};
///////////////////////something

export interface SalesDetail {
  subtotal: number;
  total: number;
  products: Product[];
  fee: number;
  grand_total: number;
  concession: Concession;
  created_at: string;
  updated_at: string;
  event: Event;
  code: string;
  sold_at: string;
  seller: Seller;
  status: number;
  tip: number;
  uuid: string;
  uuid_concession: string;
  uuid_event: string;
  uuid_seller: string;
  uuid_venue: string;
  total_products: number;
  venue: Venue;
  payments: Payment[];
  comments?: string[];
  bill?: {
    uuid: string;
    name: string;
  };
}

interface Payment {
  uuid: string;
  code: string;
  subtotal: number;
  tip: number;
  total: number;
  paid_at: string;
  status: number;
  nfc_transaction?: NfcTransaction;
  card_transaction?: CardTransaction;
  qr_transaction?: QrTransaction;
  palm_transaction?: PalmTransaction;
  cash_transaction?: CashTransaction;
}

interface PalmTransaction {
  uuid: string;
  terminal: string;
  palm_fingerprint?: {
    uuid: string;
    customer?: {
      phone_number: string;
    };
  };
}

interface CardTransaction {
  uuid: string;
  transaction_id: string;
  authorization: string;
  pin_used: number;
  approved: number;
  has_refund: number;
  rejection_reason: string;
  operation_key: string;
  afiliation: string;
  sale_key: string;
  terminal: string;
  entry_mode: string;
  longitude: string;
  latitude: string;
  type: string;
  sale_code: string;
  is_reversed: number;
  is_cancelled: number;
  date: string;
  application_date: string;
  currency: string;
  card: Card;
  created_at: string;
  merchant?: string;
}

export interface QrTransaction {
  qr_ticket: QrTicket;
  qr_wallet: QrWallet;
  subtotal: number;
  terminal: string;
  tip: number;
  total: number;
  type: string;
  uuid: string;
  venue_wallet: VenueWallet;
}

interface QrTicket {
  bin: string | null;
  current_balance: number | null;
  uuid: string | null;
}

interface QrWallet {
  customer: Customer;
  name: string | null;
  uuid: string | null;
}

interface Customer {
  last_name: string | null;
  name: string | null;
  uuid: string | null;
}

interface VenueWallet {
  uuid: string;
  venue: Venue;
}

interface Venue {
  uuid: string;
  name: string;
}

interface CashTransaction {
  uuid: string;
}

export interface SalesHistoryItem {
  concession_uuid: string;
  concession_name: string;
  event_uuid: string;
  event_name: string;
  event_starts_at: string;
  sales_quantity: number;
  sales_products_quantity: number;
  sales_subtotal: number;
}

export interface SalesHistoryResponse {
  data: SalesHistoryItem[];
}

export interface SalesHistoryQueries {
  concession?: string;
  event?: string;
  venue?: string;
}

export interface CompleteSaleBody {
  tip?: number;
  payment_type?: "card" | "cash";
}

export interface PartialCancelProduct {
  uuid_sale_product: string;
  quantity: number;
}

export interface PartialCancelPayment {
  uuid: string;
  refund_amount: number;
}

export interface PartialCancelSaleBody {
  products: PartialCancelProduct[];
  payments?: PartialCancelPayment[];
  phone_number?: string;
}
